import { useListCompetitors } from '@reelplicate/api-client/competitors';
import { useListMedia } from '@reelplicate/api-client/media';
import { useListMediaCollections } from '@reelplicate/api-client/media-collections';
import { useListPersonas } from '@reelplicate/api-client/personas';
import { useListSocialAccounts } from '@reelplicate/api-client/social-accounts';
import type { NodeDefinition } from '@reelplicate/flows';
import {
  Button,
  Checkbox,
  Combobox,
  Field,
  FilterChip,
  Inline,
  NumberStepper,
  SegmentedControl,
  SegmentedControlItem,
  Select,
  SelectItem,
  Stack,
  Tag,
  TextArea,
  TextInput,
  Toggle,
  Well,
} from '@reelplicate/ui';
import { type ReactNode, useId, useMemo, useState } from 'react';

import { timezoneOptions } from '@/features/settings/timezones';

import { humanize } from '../../catalogue-ui';
import { CaptionStyleField } from './caption-style-field';
import { configErrors, describeConfig, type FieldSpec, isShown } from './config-fields';
import { ReelScenesModal } from './reel-scenes-modal';
import { useReelsWithScenes } from './reels-with-scenes';
import { SlotsField } from './slots-field';

/** The "nothing picked" choice in an optional select. */
const NONE = '__none__';

/** Pickers load workspace lists; one failing shouldn't take the form down. */
const PICKER_QUERY = (workspaceId: string) => ({
  query: { enabled: Boolean(workspaceId), throwOnError: false },
});

type Props = {
  workspaceId: string;
  definition: NodeDefinition;
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
};

/**
 * The node's settings, drawn from its config schema: each field's widget comes
 * from `.meta({ widget })` or its type, and its error from the same schema the
 * API checks on save.
 */
export function ConfigForm({ workspaceId, definition, config, onChange }: Props) {
  const fields = useMemo(() => describeConfig(definition.config), [definition]);
  const { byField, general } = configErrors(definition.config, config);
  const setConfig = (patch: Record<string, unknown>) => {
    const next = { ...config };
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === '') delete next[key];
      else next[key] = value;
    }
    onChange(next);
  };
  const set = (key: string, value: unknown) => setConfig({ [key]: value });

  if (fields.length === 0) {
    return (
      <p className="text-body-sm text-text-secondary">
        {definition.label} has no settings.
      </p>
    );
  }
  return (
    <Stack gap={5}>
      {general.map((message) => (
        <Well key={message} tone="danger" role="status">
          {message}
        </Well>
      ))}
      {fields
        .filter((field) => isShown(field, config, fields))
        .map((field) => (
          <ConfigField
            key={field.key}
            workspaceId={workspaceId}
            field={field}
            value={config[field.key]}
            config={config}
            error={
              field.widget === 'reel'
                ? (byField.get(field.key) ?? byField.get('scenes'))
                : byField.get(field.key)
            }
            onChange={(value) => set(field.key, value)}
            setConfig={setConfig}
          />
        ))}
    </Stack>
  );
}

type FieldProps = {
  workspaceId: string;
  field: FieldSpec;
  value: unknown;
  /** The node's whole config, for fields that depend on others. */
  config?: Record<string, unknown>;
  error?: string;
  onChange: (value: unknown) => void;
  /** Sets several settings at once (a picker that fills two fields). */
  setConfig?: (patch: Record<string, unknown>) => void;
};

function ConfigField({
  workspaceId,
  field,
  value,
  config,
  error,
  onChange,
  setConfig,
}: FieldProps) {
  const { label, hint, optional, options = [] } = field;
  switch (field.widget) {
    case 'segmented':
      return (
        <Group label={label} hint={hint} error={error}>
          {(labelId) => (
            <SegmentedControl
              aria-label={label}
              aria-labelledby={labelId}
              className="w-full"
              value={
                typeof value === 'string' || typeof value === 'number'
                  ? String(value)
                  : ''
              }
              onValueChange={(next) =>
                onChange(
                  typeof value === 'number' || field.min !== undefined
                    ? Number(next)
                    : next,
                )
              }
            >
              {options.map((option) => (
                <SegmentedControlItem
                  key={option}
                  value={option}
                  className="flex-1 justify-center px-2"
                >
                  {humanize(option)}
                </SegmentedControlItem>
              ))}
            </SegmentedControl>
          )}
        </Group>
      );
    case 'chips': {
      const selected = new Set(
        Array.isArray(value)
          ? (value as string[])
          : Array.isArray(field.defaultValue)
            ? (field.defaultValue as string[])
            : [],
      );
      return (
        <Group label={label} hint={hint} error={error}>
          {() => (
            <div className="flex flex-wrap gap-1.5">
              {options.map((option) => (
                <FilterChip
                  key={option}
                  selected={selected.has(option)}
                  onSelectedChange={(on) =>
                    onChange(options.filter((o) => (o === option ? on : selected.has(o))))
                  }
                >
                  {humanize(option)}
                </FilterChip>
              ))}
            </div>
          )}
        </Group>
      );
    }
    case 'toggle':
      return (
        <Stack gap={1}>
          <Toggle
            label={label}
            checked={value === true}
            onCheckedChange={(on) => onChange(on)}
          />
          {hint && <p className="text-caption text-text-secondary">{hint}</p>}
        </Stack>
      );
    case 'number':
    case 'days':
      return (
        <Field
          label={field.widget === 'days' ? `${label} (days)` : label}
          optional={optional}
          helper={hint}
          error={error}
        >
          <NumberStepper
            size="sm"
            value={typeof value === 'number' ? value : null}
            min={field.min}
            max={field.max}
            onValueChange={(next) => onChange(next ?? undefined)}
          />
        </Field>
      );
    case 'decimal':
    case 'multiplier':
      return (
        <Field
          label={field.widget === 'multiplier' ? `${label} (×)` : label}
          optional={optional}
          helper={hint}
          error={error}
        >
          <TextInput
            size="sm"
            type="number"
            inputMode="decimal"
            step="any"
            min={field.min}
            max={field.max}
            value={typeof value === 'number' ? value : ''}
            onChange={(event) =>
              onChange(event.target.value === '' ? undefined : Number(event.target.value))
            }
          />
        </Field>
      );
    case 'textarea':
      return (
        <Field label={label} optional={optional} helper={hint} error={error}>
          <TextArea
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => onChange(event.target.value)}
          />
        </Field>
      );
    case 'prompt':
      return (
        <PromptField field={field} value={value} error={error} onChange={onChange} />
      );
    case 'select':
      return (
        <Field label={label} optional={optional} helper={hint} error={error}>
          <Select
            size="sm"
            value={typeof value === 'string' ? value : optional ? NONE : undefined}
            onValueChange={(next) => onChange(next === NONE ? undefined : next)}
          >
            {/* Optional: leaving it empty is a choice too (e.g. the workspace default). */}
            {optional && <SelectItem value={NONE}>Default</SelectItem>}
            {options.map((option) => (
              <SelectItem key={option} value={option}>
                {humanize(option)}
              </SelectItem>
            ))}
          </Select>
        </Field>
      );
    case 'schedule':
      return (
        <Field
          label={label}
          helper={
            error
              ? undefined
              : 'Cron: minute hour day month weekday. 0 9 * * 1 is every Monday at 09:00.'
          }
          error={error}
        >
          <TextInput
            size="sm"
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => onChange(event.target.value)}
          />
        </Field>
      );
    case 'datetime':
      return (
        <Field label={label} optional={optional} helper={hint} error={error}>
          <TextInput
            size="sm"
            type="datetime-local"
            value={typeof value === 'string' ? toLocalInput(value) : ''}
            onChange={(event) =>
              onChange(
                event.target.value
                  ? new Date(event.target.value).toISOString()
                  : undefined,
              )
            }
          />
        </Field>
      );
    case 'date':
      return (
        <Field label={label} optional={optional} helper={hint} error={error}>
          <TextInput
            size="sm"
            type="date"
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => onChange(event.target.value || undefined)}
          />
        </Field>
      );
    case 'times':
      return <TimesField field={field} value={value} error={error} onChange={onChange} />;
    case 'tags':
      return <TagsField field={field} value={value} error={error} onChange={onChange} />;
    case 'timeZone':
      return (
        <TimeZoneField field={field} value={value} error={error} onChange={onChange} />
      );
    case 'persona':
      return (
        <PersonaField
          workspaceId={workspaceId}
          field={field}
          value={value}
          error={error}
          onChange={onChange}
        />
      );
    case 'slots':
      return (
        <SlotsField
          workspaceId={workspaceId}
          field={field}
          value={value}
          carouselStyle={typeof config?.style === 'string' ? config.style : undefined}
          error={error}
          onChange={onChange}
        />
      );
    case 'captionStyle':
      return (
        <CaptionStyleField
          workspaceId={workspaceId}
          field={field}
          value={value}
          config={config}
          onChange={onChange}
        />
      );
    case 'collection':
      return (
        <CollectionField
          workspaceId={workspaceId}
          field={field}
          value={value}
          error={error}
          onChange={onChange}
        />
      );
    case 'image':
      return (
        <ImageField
          workspaceId={workspaceId}
          field={field}
          value={value}
          error={error}
          onChange={onChange}
        />
      );
    case 'reel':
      return (
        <ReelScenesField
          workspaceId={workspaceId}
          field={field}
          value={value}
          config={config}
          error={error}
          onChange={onChange}
          setConfig={setConfig}
        />
      );
    case 'scenes':
      // Picked with the reel (ReelScenesField).
      return null;
    case 'audioTrack':
      return (
        <AudioTrackField
          workspaceId={workspaceId}
          field={field}
          value={value}
          error={error}
          onChange={onChange}
        />
      );
    case 'accounts':
      return (
        <AccountsField
          workspaceId={workspaceId}
          field={field}
          value={value}
          error={error}
          onChange={onChange}
        />
      );
    case 'personas':
    case 'personaLooks':
      return (
        <PersonasField
          workspaceId={workspaceId}
          needs={field.widget === 'personaLooks' ? 'look' : 'accounts'}
          field={field}
          value={value}
          error={error}
          onChange={onChange}
        />
      );
    case 'competitors':
      return (
        <CompetitorsField
          workspaceId={workspaceId}
          field={field}
          value={value}
          error={error}
          onChange={onChange}
        />
      );
    case 'text':
      return (
        <Field label={label} optional={optional} helper={hint} error={error}>
          <TextInput
            size="sm"
            value={typeof value === 'string' || typeof value === 'number' ? value : ''}
            onChange={(event) => onChange(event.target.value)}
          />
        </Field>
      );
    case 'unsupported':
      return (
        <Group label={label} hint={hint} error={error}>
          {() => (
            <p className="text-body-sm text-text-secondary">
              Picking this from your workspace comes with the next release.
            </p>
          )}
        </Group>
      );
  }
}

/** A labelled group for controls that aren’t one input (segments, chips, lists): label, then its explanation underneath. */
function Group({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: (labelId: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div role="group" aria-labelledby={id} className="flex flex-col gap-1.5">
      <div className="flex flex-col gap-0.5">
        <span id={id} className="text-label-md text-text-primary">
          {label}
        </span>
        {hint && <span className="text-caption text-text-secondary">{hint}</span>}
      </div>
      {children(id)}
      {error && <p className="text-caption text-status-danger-fg">{error}</p>}
    </div>
  );
}

function PersonaField({ workspaceId, field, value, error, onChange }: FieldProps) {
  const { data } = useListPersonas(workspaceId, PICKER_QUERY(workspaceId));
  return (
    <Field
      label={field.label}
      optional={field.optional}
      helper={field.hint}
      error={error}
    >
      <Select
        size="sm"
        placeholder={data?.items.length === 0 ? 'No personas yet' : 'Pick a persona'}
        value={typeof value === 'string' ? value : field.optional ? NONE : undefined}
        onValueChange={(next) => onChange(next === NONE ? undefined : next)}
      >
        {field.optional && <SelectItem value={NONE}>No persona</SelectItem>}
        {data?.items.map((persona) => (
          <SelectItem key={persona.id} value={persona.id}>
            {`${persona.name} · ${
              persona.referenceCount
                ? `${persona.referenceCount} of 5 reference shots`
                : 'no reference look'
            }`}
          </SelectItem>
        ))}
      </Select>
    </Field>
  );
}

function CollectionField({ workspaceId, field, value, error, onChange }: FieldProps) {
  const { data } = useListMediaCollections(workspaceId, PICKER_QUERY(workspaceId));
  return (
    <Field
      label={field.label}
      optional={field.optional}
      helper={field.hint}
      error={error}
    >
      <Select
        size="sm"
        placeholder="Whole library"
        value={typeof value === 'string' ? value : NONE}
        onValueChange={(next) => onChange(next === NONE ? undefined : next)}
      >
        <SelectItem value={NONE}>Whole library</SelectItem>
        {data?.map((collection) => (
          <SelectItem key={collection.id} value={collection.id}>
            {collection.name}
          </SelectItem>
        ))}
      </Select>
    </Field>
  );
}

/** One image from the library, searched by title, with its persona when it has one. */
function ImageField({ workspaceId, field, value, error, onChange }: FieldProps) {
  const { data } = useListMedia(workspaceId, PICKER_QUERY(workspaceId));
  const { data: personas } = useListPersonas(workspaceId, PICKER_QUERY(workspaceId));
  const options = useMemo(() => {
    const names = new Map((personas?.items ?? []).map((p) => [p.id, p.name]));
    return (data?.items ?? [])
      .filter((asset) => asset.kind === 'IMAGE' && asset.fileUrl)
      .map((asset) => ({
        value: asset.id,
        label: asset.title,
        avatar: (
          <img
            src={asset.thumbnailUrl ?? asset.fileUrl!}
            alt=""
            className="size-6 shrink-0 rounded-xs object-cover"
          />
        ),
        meta: asset.personaId ? names.get(asset.personaId) : undefined,
      }));
  }, [data, personas]);
  return (
    <Field
      label={field.label}
      optional={field.optional}
      helper={field.hint}
      error={error}
    >
      <Combobox
        options={options}
        value={typeof value === 'string' ? value : null}
        onValueChange={onChange}
        placeholder="Search your images"
        emptyTitle={(query) =>
          query ? `No image matches “${query}”` : 'No images in your library yet'
        }
      />
    </Field>
  );
}

/**
 * Reel scenes' reel and scenes, set together in a picker: a summary of what's
 * picked, with the frames of the scenes it will remake.
 */
function ReelScenesField({ workspaceId, field, config, error, setConfig }: FieldProps) {
  const [open, setOpen] = useState(false);
  const { reels } = useReelsWithScenes(workspaceId);
  const reelId =
    typeof config?.mediaAssetId === 'string' ? config.mediaAssetId : undefined;
  const picked = Array.isArray(config?.scenes) ? (config.scenes as number[]) : [];
  const reel = reels.find((r) => r.id === reelId);
  const scenes = reel?.scenes ?? [];
  const shown = picked.length ? scenes.filter((s) => picked.includes(s.scene)) : scenes;
  return (
    <Field label={field.label} error={error}>
      <Stack
        gap={3}
        className={`rounded-lg border p-3 ${reel ? 'border-border-subtle' : 'border-border-subtle border-dashed'}`}
      >
        {reel ? (
          <>
            <Inline gap={3} alignY="center">
              {reel.thumbnailUrl && (
                <img
                  src={reel.thumbnailUrl}
                  alt=""
                  className="aspect-[9/16] w-8 shrink-0 rounded-xs object-cover"
                />
              )}
              <Stack gap={0.5} className="min-w-0 flex-1">
                <span className="text-body-sm text-text-primary truncate">
                  {reel.title}
                </span>
                <span className="text-caption text-text-secondary">
                  {picked.length
                    ? `${picked.length} of ${scenes.length} scenes`
                    : `All ${scenes.length} scenes`}
                </span>
              </Stack>
            </Inline>
            <Inline gap={1} aria-label="Picked scenes">
              {shown
                .slice(0, 6)
                .map((scene) =>
                  scene.frameUrl ? (
                    <img
                      key={scene.scene}
                      src={scene.frameUrl}
                      alt={`Scene ${scene.scene}`}
                      className="aspect-[9/16] w-7 rounded-xs object-cover"
                    />
                  ) : null,
                )}
              {shown.length > 6 && (
                <span className="text-caption text-text-tertiary self-center">
                  +{shown.length - 6}
                </span>
              )}
            </Inline>
          </>
        ) : (
          <span className="text-body-sm text-text-secondary">
            {reelId ? 'That reel is no longer in your library.' : 'No reel picked'}
          </span>
        )}
        <Button
          size="sm"
          variant="outline"
          className="self-start"
          onClick={() => setOpen(true)}
        >
          {reel ? 'Change' : 'Choose reel & scenes'}
        </Button>
      </Stack>
      <ReelScenesModal
        open={open}
        onOpenChange={setOpen}
        workspaceId={workspaceId}
        value={{ mediaAssetId: reelId, scenes: picked }}
        onPick={(pick) => setConfig?.(pick)}
      />
    </Field>
  );
}

/**
 * A generation prompt, shown as it will run: the default until edited.
 * Saving text equal to the default clears it, so improved defaults still
 * reach nodes nobody changed.
 */
function PromptField({ field, value, error, onChange }: Omit<FieldProps, 'workspaceId'>) {
  const prefill = field.prefill ?? '';
  const edited = typeof value === 'string' && value.trim() !== '' && value !== prefill;
  return (
    <Field label={field.label} helper={field.hint} error={error}>
      <Stack gap={1}>
        <TextArea
          rows={6}
          value={typeof value === 'string' ? value : prefill}
          onChange={(event) =>
            onChange(event.target.value === prefill ? undefined : event.target.value)
          }
        />
        {edited && (
          <Button
            size="sm"
            variant="ghost"
            className="self-start"
            onClick={() => onChange(undefined)}
          >
            Reset to default
          </Button>
        )}
      </Stack>
    </Field>
  );
}

/** An audio file from the library (Sound's brand track). */
function AudioTrackField({ workspaceId, field, value, error, onChange }: FieldProps) {
  const { data } = useListMedia(workspaceId, PICKER_QUERY(workspaceId));
  const tracks = data?.items.filter((asset) => asset.kind === 'AUDIO') ?? [];
  return (
    <Field
      label={field.label}
      optional={field.optional}
      helper={field.hint}
      error={error}
    >
      <Select
        size="sm"
        placeholder={
          data && tracks.length === 0 ? 'No audio in your library' : 'Pick a track'
        }
        value={typeof value === 'string' ? value : undefined}
        onValueChange={onChange}
      >
        {tracks.map((track) => (
          <SelectItem key={track.id} value={track.id}>
            {track.title}
          </SelectItem>
        ))}
      </Select>
    </Field>
  );
}

const PLATFORM_NAMES: Record<string, string> = {
  TIKTOK: 'TikTok',
  INSTAGRAM: 'Instagram',
  YOUTUBE: 'YouTube',
  LINKEDIN: 'LinkedIn',
};

function AccountsField({ workspaceId, field, value, error, onChange }: FieldProps) {
  const { data: accounts } = useListSocialAccounts(
    workspaceId,
    PICKER_QUERY(workspaceId),
  );
  const { data: personas } = useListPersonas(workspaceId, PICKER_QUERY(workspaceId));
  const selected = new Set(Array.isArray(value) ? (value as string[]) : []);
  const groups = [
    { id: null, name: 'Brand' },
    ...(personas?.items ?? []).map((persona) => ({ id: persona.id, name: persona.name })),
  ]
    .map((group) => ({
      ...group,
      accounts: (accounts ?? []).filter((a) => a.personaId === group.id),
    }))
    .filter((group) => group.accounts.length > 0);

  return (
    <Group
      label={field.label}
      hint={accounts ? `${selected.size} of ${accounts.length} selected` : undefined}
      error={error}
    >
      {() =>
        accounts?.length === 0 ? (
          <p className="text-body-sm text-text-secondary">
            Add accounts in Settings → Connected accounts to publish here.
          </p>
        ) : (
          <Stack gap={3}>
            {groups.map((group) => (
              <Stack key={group.id ?? 'brand'} gap={2}>
                <span className="text-caption text-text-secondary">{group.name}</span>
                {group.accounts.map((account) => (
                  <Checkbox
                    key={account.id}
                    label={`@${account.handle} · ${PLATFORM_NAMES[account.platform] ?? account.platform}`}
                    checked={selected.has(account.id)}
                    onCheckedChange={(on) => {
                      const next = new Set(selected);
                      if (on === true) next.add(account.id);
                      else next.delete(account.id);
                      onChange([...next]);
                    }}
                  />
                ))}
              </Stack>
            ))}
          </Stack>
        )
      }
    </Group>
  );
}

/**
 * Personas to post for (ones without accounts can't be ticked), or to put in
 * images (ones without a reference look can't).
 */
function PersonasField({
  workspaceId,
  field,
  value,
  error,
  onChange,
  needs,
}: FieldProps & { needs: 'accounts' | 'look' }) {
  const { data } = useListPersonas(workspaceId, PICKER_QUERY(workspaceId));
  const personas = data?.items ?? [];
  const selected = new Set(Array.isArray(value) ? (value as string[]) : []);
  const detail = (persona: (typeof personas)[number]) => {
    if (needs === 'look') {
      return persona.referenceCount
        ? `${persona.referenceCount} of 5 reference shots`
        : 'no reference look';
    }
    if (persona.accountCount === 1) return '1 account';
    return persona.accountCount ? `${persona.accountCount} accounts` : 'no accounts yet';
  };
  const usable = (persona: (typeof personas)[number]) =>
    needs === 'look' ? persona.referenceCount > 0 : persona.accountCount > 0;
  return (
    <Group
      label={field.label}
      hint={
        selected.size
          ? `${selected.size} ticked`
          : needs === 'look'
            ? 'All with a reference look'
            : 'All with accounts'
      }
      error={error}
    >
      {() =>
        data && personas.length === 0 ? (
          <p className="text-body-sm text-text-secondary">
            Add personas in Personas to {needs === 'look' ? 'show them' : 'post as them'}.
          </p>
        ) : (
          <Stack gap={2}>
            {personas.map((persona) => (
              <Checkbox
                key={persona.id}
                label={`${persona.name} · ${detail(persona)}`}
                disabled={!usable(persona)}
                checked={selected.has(persona.id)}
                onCheckedChange={(on) => {
                  const next = new Set(selected);
                  if (on === true) next.add(persona.id);
                  else next.delete(persona.id);
                  onChange([...next]);
                }}
              />
            ))}
            {field.hint && (
              <p className="text-caption text-text-secondary">{field.hint}</p>
            )}
          </Stack>
        )
      }
    </Group>
  );
}

function CompetitorsField({ workspaceId, field, value, error, onChange }: FieldProps) {
  const { data } = useListCompetitors(workspaceId, PICKER_QUERY(workspaceId));
  const competitors = data?.items ?? [];
  // Picked by id; flows saved before competitors could lack a site hold sites.
  const saved = new Set(Array.isArray(value) ? (value as string[]) : []);
  const isPicked = (c: { id: string; website: string | null }) =>
    saved.has(c.id) || (c.website !== null && saved.has(c.website));
  return (
    <Group label={field.label} hint={field.hint} error={error}>
      {() =>
        competitors.length === 0 ? (
          <p className="text-body-sm text-text-secondary">
            Add competitors in Settings → Competitors.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {competitors.map((competitor) => (
              <FilterChip
                key={competitor.id}
                selected={isPicked(competitor)}
                onSelectedChange={(on) =>
                  onChange(
                    competitors
                      .filter((c) => (c.id === competitor.id ? on : isPicked(c)))
                      .map((c) => c.id),
                  )
                }
              >
                {competitor.label}
              </FilterChip>
            ))}
          </div>
        )
      }
    </Group>
  );
}

/** An ISO time as the value a datetime-local input wants (local time, minutes). */
/** Clock times, e.g. 09:00 and 18:00: a tag each, and a time input to add one. */
function TimesField({ field, value, error, onChange }: Omit<FieldProps, 'workspaceId'>) {
  const [draft, setDraft] = useState('');
  const times = Array.isArray(value)
    ? (value as string[])
    : ((field.defaultValue as string[] | undefined) ?? []);
  const add = () => {
    if (!draft || times.includes(draft)) return;
    onChange([...times, draft].sort());
    setDraft('');
  };
  return (
    <Group label={field.label} hint={field.hint} error={error}>
      {() => (
        <Stack gap={2}>
          {times.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {times.map((time) => (
                <Tag
                  key={time}
                  removeLabel={`Remove ${time}`}
                  onRemove={() => onChange(times.filter((t) => t !== time))}
                >
                  {time}
                </Tag>
              ))}
            </div>
          )}
          <Inline gap={2} alignY="center">
            <TextInput
              size="sm"
              type="time"
              aria-label="New time"
              className="min-w-0 flex-1"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  add();
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!draft}
              onClick={add}
            >
              Add time
            </Button>
          </Inline>
        </Stack>
      )}
    </Group>
  );
}

/** Free-text tags: a tag each, and an input to add one (Enter or Add). */
function TagsField({ field, value, error, onChange }: Omit<FieldProps, 'workspaceId'>) {
  const [draft, setDraft] = useState('');
  const tags = Array.isArray(value) ? (value as string[]) : [];
  const add = () => {
    const tag = draft.trim();
    if (!tag || tags.includes(tag)) return;
    onChange([...tags, tag]);
    setDraft('');
  };
  return (
    <Group label={field.label} hint={field.hint} error={error}>
      {() => (
        <Stack gap={2}>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <Tag
                  key={tag}
                  removeLabel={`Remove ${tag}`}
                  onRemove={() => onChange(tags.filter((t) => t !== tag))}
                >
                  {tag}
                </Tag>
              ))}
            </div>
          )}
          <Inline gap={2} alignY="center">
            <TextInput
              size="sm"
              aria-label="New tag"
              placeholder="e.g. hook-base"
              className="min-w-0 flex-1"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  add();
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!draft.trim()}
              onClick={add}
            >
              Add tag
            </Button>
          </Inline>
        </Stack>
      )}
    </Group>
  );
}

/** An IANA time zone; empty uses the workspace's. */
/** The "no zone of its own" choice; not an IANA name, so it can't clash. */
const NO_ZONE = 'none';

function TimeZoneField({
  field,
  value,
  error,
  onChange,
}: Omit<FieldProps, 'workspaceId'>) {
  // First in the list, so a zone picked once can be taken off again.
  const zones = useMemo(
    () => [
      { value: NO_ZONE, label: 'Each persona’s, else the workspace’s' },
      ...timezoneOptions(),
    ],
    [],
  );
  return (
    <Field
      label={field.label}
      optional={field.optional}
      helper={field.hint}
      error={error}
    >
      <Combobox
        options={zones}
        value={typeof value === 'string' && value ? value : NO_ZONE}
        onValueChange={(next) => onChange(next === NO_ZONE ? undefined : next)}
        placeholder="Each persona’s, else the workspace’s"
        emptyTitle={(query) => `No time zone matches “${query}”`}
      />
    </Field>
  );
}

function toLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}
